import { Component, DestroyRef, inject, effect, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CdkDragDrop, CdkDropList, CdkDrag, CdkDragHandle, CdkDragPlaceholder, moveItemInArray } from '@angular/cdk/drag-drop';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { CanComponentDeactivate } from 'src/app/guards/can-deactivate.guard';
import { AppStateService } from 'src/app/shared/services/helpers/app-state.service';
import * as _ from 'lodash';
import * as moment from 'moment';
import { ActionService } from 'src/app/shared/services/action/action.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SettingsService } from 'src/app/shared/services/settings';
import { DynamicActionFormRendererComponent } from 'src/app/shared/components/dynamic-action-form-renderer/dynamic-action-form-renderer.component';
import { LtmsLoaderComponent } from 'src/app/shared/components/ltms-loader/ltms-loader.component';
import { statusSchema, statusRuleSchema } from '../../constants/schemas';
// Declare Helpers globally
declare const Helpers: any;
declare const Utils: any;

interface Status {
  uuid: string;
  name: string;
  sequence: number | null;
  out_of_sequence: boolean;
  color: string;
  is_enabled: boolean;
  is_deleted?: boolean;
  exclude_from_ageing?: boolean;
  trigger_for?: string[];
  hide_slaTimer_for_status?: boolean;
  access_key_rules?: any[];
  rules?: Rule[];
  visible?: boolean;
  levels?: string[];
}

interface Rule {
  id: string;
  name: string;
  description: string;
  active: boolean;
  is_enabled: boolean;
  created_date: Date | string;
  last_updated_on: Date | string;
  updated_by?: string;
  formatted_created_date?: string;
  formatted_last_updated?: string;
  status_list?: string[];
  conditions?: any;
  type?: string;
  is_deleted?: boolean;
}

interface StatusList {
  sequenced: Status[];
  unsequenced: Status[];
}

interface Pagination {
  statusRulesPageNo: number;
  statusRulesPageFrom: number;
  statusRulesPageTo: number;
  statusRulesCount: number;
  limit: number;
  disableNext: boolean;
  disablePrev: boolean;
  isNextCtrl: boolean;
}

@Component({
  selector: 'app-litmus-action-status',
  templateUrl: './litmus-action-status.component.html',
  styleUrls: ['./litmus-action-status.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, DynamicActionFormRendererComponent, LtmsLoaderComponent, CdkDropList, CdkDrag, CdkDragHandle, CdkDragPlaceholder]
})
export class LitmusActionStatusComponent implements OnInit, CanComponentDeactivate {
  @Input() readonly: boolean = true;

  destroyRef = inject(DestroyRef);

  // Settings and view mode
  settings = { selected: 'basic' };
  viewMode: 'basic' | 'advanced' = 'basic';
  callee: string = 'lwStatusSettings';

  // Loading and state management
  pagerBusy = true;
  isSaving = false;
  isCancelling = false;
  isSaveEnabled = false;

  // Modal state
  isModalOpen = false;
  modalSchema: any = {};
  defaultValues: any = {};
  selectedStatusDetails: Status | null = null;
  selectedRuleDetails: Rule | null = null;

  // Brand and configuration
  brandId: string = '';
  brandDetails: any = {};
  configurationLoaded: boolean = false;
  tableRows: any = [];
  hierarchyMapping: any = {};

  // Data storage
  statusList: StatusList = {
    sequenced: [],
    unsequenced: []
  };
  statusListBackup: Status[] = [];
  statusRules: Rule[] = [];
  statusRulesBackup: Rule[] = [];
  filteredStatusRules: Rule[] = [];
  managersMapping: any = {};

  // Search
  textToSearch: string = '';
  lwActionStatusBasicSearch: string = '';
  lwActionStatusAdvancedSearch: string = '';

  // Pagination
  pagination: Pagination = {
    statusRulesPageNo: 0,
    statusRulesPageFrom: 1,
    statusRulesPageTo: 0,
    statusRulesCount: 0,
    limit: 10,
    disableNext: true,
    disablePrev: true,
    isNextCtrl: true
  };

  // Workflow rules fields and operators from API
  workflowFields: any[] = [];
  workflowOperators: any[] = [];
  ruleSetIdsToExclude: any[] = ['lw_event_fields_logic_ruleset', 'response_fields_logic_mandatory_ruleset', 'lw_manager_fields_logic_ruleset'];

  // Status modal schema
  statusModalSchema = _.cloneDeep(statusSchema);

  // Rule modal schema
  ruleModalSchema = _.cloneDeep(statusRuleSchema);
  fieldDefsMap: any = {}
  constructor(
    private _toast: ToastService,
    private stateService: AppStateService,
    private actionService: ActionService,
    private _settingsService: SettingsService
  ) {
    effect(() => {
      if (this.stateService.initialized()) {
        this.fieldDefsMap = {}
        const brandDetail: any = this.stateService.brandDetails();
        if (!_.isEmpty(brandDetail)) {
          this.brandDetails = brandDetail;
          this.brandId = brandDetail._id;
          if (brandDetail.field_definitions && !_.isEmpty(brandDetail.field_definitions)) {
            brandDetail.field_definitions.forEach((field: any) => {
              this.fieldDefsMap[field.field_id] = field;
            })
          }
          this.getStatusList();
          this.configurationLoaded = true;
        }
      }
    });
  }

  ngOnInit() {
    this.resetData();
  }

  get isConfigurationReady(): boolean {
    return this.configurationLoaded && !this.pagerBusy;
  }

  /**
   * Reset all data to initial state
   */
  private resetData(): void {
    this.pagination = {
      statusRulesPageNo: 0,
      statusRulesPageFrom: 1,
      statusRulesPageTo: 0,
      statusRulesCount: 0,
      limit: 10,
      disableNext: true,
      disablePrev: true,
      isNextCtrl: true
    };
    this.textToSearch = '';
    this.settings = { selected: 'basic' };
    this.callee = 'lwStatusSettings';
    this.statusList = { sequenced: [], unsequenced: [] };
    this.statusListBackup = [];
    this.statusRules = [];
    this.statusRulesBackup = [];
    this.filteredStatusRules = [];
    this.lwActionStatusBasicSearch = '';
    this.lwActionStatusAdvancedSearch = '';
  }

  /**
   * Get status list from API
   */
  private getStatusList(): void {
    if (!this.brandId) return;

    this.pagerBusy = true;
    this._settingsService.getStatusByBrandId(this.brandId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.pagerBusy = false;
          if (response && response.data && response.data.status_list) {
            const statusList = response.data.status_list;
            const managers = response.data.managers || [];

            // Create managers mapping
            this.managersMapping = {};
            managers.forEach((manager: any) => {
              if (!_.isEmpty(manager)) {
                this.managersMapping[manager._id] = manager;
              }
            });

            // Add default properties to statuses
            this.addDefaultPropertiesToStatus(statusList, (processedList: Status[]) => {
              // Filter out deleted statuses
              this.statusListBackup = _.filter(_.cloneDeep(processedList), (status: Status) => {
                return !status.is_deleted;
              });

              // Prepare rules data
              this.getAdvancedData(this.statusListBackup, managers);

              // Separate statuses based on sequence
              this.separateStatusBasedOnSequence(this.statusListBackup);
            });
          } else {
            this.statusListBackup = [];
            this.statusList = { sequenced: [], unsequenced: [] };
          }
        },
        error: (error: any) => {
          console.error('Error fetching status list:', error);
          this.pagerBusy = false;
          this.statusListBackup = [];
          this.statusList = { sequenced: [], unsequenced: [] };
        }
      });
  }

  /**
   * Add default properties to standard statuses
   */
  private addDefaultPropertiesToStatus(statusList: Status[], callback: (list: Status[]) => void): void {
    if (!statusList) {
      callback([]);
      return;
    }
    this.prepareHierarchyMapping();

    _.each(statusList, (status: Status) => {
      if (!status.out_of_sequence) {
        const statusName = (status.name || '').toLowerCase();
        switch (statusName) {
          case 'open':
            if (!status.color) status.color = '#c09853';
            if (!status.sequence) status.sequence = 1;
            if (status.is_enabled !== false) status.is_enabled = true;
            break;
          case 'in-progress':
            if (!status.color) status.color = '#5a5a5a';
            if (!status.sequence) status.sequence = 2;
            if (status.is_enabled !== false) status.is_enabled = true;
            break;
          case 'on-hold':
            if (!status.color) status.color = '#b94a48';
            if (!status.sequence) status.sequence = 3;
            if (status.is_enabled !== false) status.is_enabled = true;
            break;
          case 'closed':
            if (!status.color) status.color = '#468847';
            if (!status.sequence) status.sequence = 4;
            if (status.is_enabled !== false) status.is_enabled = true;
            break;
        }
      }
    });

    callback(statusList);
  }

  /**
   * Separate statuses into sequenced and unsequenced
   */
  private separateStatusBasedOnSequence(list: Status[]): void {
    this.statusList.sequenced = _.filter(list, (status: Status) => {
      return !status.out_of_sequence;
    });
    this.statusList.sequenced = this.sortBySequence('sequence', this.statusList.sequenced);

    this.statusList.unsequenced = _.filter(list, (status: Status) => {
      return status.out_of_sequence === true;
    });
  }

  /**
   * Sort list by sequence
   */
  private sortBySequence(key: string, list: any[], params?: any): any[] {
    try {
      let value: any = null;
      const sortedList = _.sortBy(list, (item: any) => {
        value = (params && params.is_date) ? new Date(item[key]).getTime() : item[key];
        return (params && params.asc === true) ? -value : value;
      });
      const isToSortString = !(params && params.is_date) && (params && params.asc === true);
      return isToSortString ? sortedList.reverse() : sortedList;
    } catch (e) {
      return list;
    }
  }

  /**
   * Get advanced data (rules) from statuses
   */
  private getAdvancedData(statusList: Status[], managers: any[]): void {
    const statusRulesMap: any = {
      ruleDetail: {},
      statuses: {}
    };
    const statusRules: Rule[] = [];

    // Create managers mapping
    this.managersMapping = {};
    _.each(managers, (manager: any) => {
      if (!_.isEmpty(manager)) {
        this.managersMapping[manager._id] = manager;
      }
    });

    // Extract rules from all statuses
    _.each(statusList, (status: Status) => {
      if (status.rules) {
        _.each(status.rules, (rule: Rule) => {
          if (!rule.is_deleted) {
            statusRulesMap.ruleDetail[rule.id] = rule;
            if (!statusRulesMap.statuses[rule.id]) {
              statusRulesMap.statuses[rule.id] = [];
            }
            statusRulesMap.statuses[rule.id].push(status.uuid);
          }
        });
      }
    });

    // Create rule objects with status lists
    for (const ruleId in statusRulesMap.statuses) {
      const rulesFor = {
        status_list: statusRulesMap.statuses[ruleId]
      };
      const ruleAndStatusDetail = Object.assign(statusRulesMap.ruleDetail[ruleId], rulesFor);

      if (!ruleAndStatusDetail.last_updated_on) {
        ruleAndStatusDetail.last_updated_on = ruleAndStatusDetail.created_date;
      }

      // Format dates
      ruleAndStatusDetail.formatted_created_date = moment(ruleAndStatusDetail.created_date).format('DD-MMM-YY HH:mm');
      ruleAndStatusDetail.formatted_last_updated = moment(ruleAndStatusDetail.last_updated_on).format('DD-MMM-YY HH:mm');

      // Add manager info to last updated
      if (ruleAndStatusDetail.updated_by && this.managersMapping[ruleAndStatusDetail.updated_by]) {
        const manager = this.managersMapping[ruleAndStatusDetail.updated_by];
        if (manager.name && manager.name !== '') {
          ruleAndStatusDetail.formatted_last_updated = ruleAndStatusDetail.formatted_last_updated + ' by ' + manager.name;
        } else if (manager.email_address && manager.email_address !== '') {
          ruleAndStatusDetail.formatted_last_updated = ruleAndStatusDetail.formatted_last_updated + ' by ' + manager.email_address;
        }
      }

      statusRules.push(ruleAndStatusDetail);
    }

    this.statusRulesBackup = _.cloneDeep(statusRules);
    this.statusRulesBackup = this.sortBySequence('created_date', this.statusRulesBackup, { is_date: true, asc: true });
    this.pagination.statusRulesCount = this.statusRulesBackup.length || 0;
    this.getRulePageWise();
  }

  /**
   * Get rules page by page
   */
  private getRulePageWise(): void {
    let statusRulesBackup = _.cloneDeep(this.statusRulesBackup);
    if (this.textToSearch && this.textToSearch !== '') {
      statusRulesBackup = _.cloneDeep(this.filteredStatusRules);
    }

    let pageNo = this.pagination.statusRulesPageNo;
    if (this.pagination.isNextCtrl) {
      this.pagination.statusRulesPageNo = this.pagination.statusRulesPageNo + 1;
      pageNo = this.pagination.statusRulesPageNo;
    }

    const getDataFrom = ((pageNo - 1) * this.pagination.limit);
    this.pagination.statusRulesCount = statusRulesBackup.length;
    this.statusRules = statusRulesBackup.splice(getDataFrom, this.pagination.limit);

    if (this.statusRules && this.statusRules.length && (this.statusRules.length === this.pagination.limit)) {
      this.pagination.statusRulesPageFrom = ((pageNo - 1) * this.pagination.limit) + 1;
      this.pagination.statusRulesPageTo = pageNo * this.pagination.limit;
      this.pagination.disableNext = (this.pagination.statusRulesPageTo === this.pagination.statusRulesCount);
      this.pagination.disablePrev = (this.pagination.statusRulesPageNo > 1);
    } else if (this.statusRules && this.statusRules.length && (this.statusRules.length < this.pagination.limit)) {
      this.pagination.statusRulesPageFrom = ((pageNo - 1) * this.pagination.limit) + 1;
      this.pagination.statusRulesPageTo = ((pageNo * this.pagination.limit) - (this.pagination.limit - this.statusRules.length));
      this.pagination.disableNext = true;
      this.pagination.disablePrev = (this.pagination.statusRulesPageNo === 1);
    }
  }

  /**
   * Navigate rules pagination
   */
  getRules(isNext: boolean): void {
    this.pagination.isNextCtrl = isNext;
    if (!isNext) {
      this.pagination.statusRulesPageNo--;
      if (this.pagination.statusRulesPageNo === 1) {
        this.pagination.disablePrev = true;
      }
    }
    this.getRulePageWise();
  }

  /**
   * Set view mode (basic/advanced)
   */
  setViewMode(mode: 'basic' | 'advanced'): void {
    this.viewMode = mode;
    this.settings.selected = mode;
    this.getDivBySettingType(mode);
  }

  /**
   * Get div by setting type
   */
  private getDivBySettingType(type: 'basic' | 'advanced'): void {
    this.settings.selected = type;
    this.textToSearch = '';
    if (type === 'basic') {
      this.callee = 'lwStatusBasicSettings';
      this.resetBasicSettings();
    } else if (type === 'advanced') {
      this.callee = 'lwStatusAdvancedSettings';
      this.resetAdvancedSettings();
    }
  }

  /**
   * Reset basic settings
   */
  private resetBasicSettings(): void {
    this.lwActionStatusBasicSearch = '';
    this.separateStatusBasedOnSequence(_.cloneDeep(this.statusListBackup));
  }

  /**
   * Reset advanced settings
   */
  private resetAdvancedSettings(): void {
    this.lwActionStatusAdvancedSearch = '';
    this.textToSearch = '';
    this.filteredStatusRules = [];
    this.pagination.statusRulesPageNo = 0;
    this.statusRules = _.cloneDeep(this.statusRulesBackup);
    this.getRulePageWise();
  }

  /**
   * Search statuses or rules
   */
  searchStatus(textToSearch: string, settingType: string): void {
    this.filteredStatusRules = [];
    this.textToSearch = '';
    this.pagination.statusRulesPageNo = 0;

    if (textToSearch && textToSearch.length) {
      const searchText = textToSearch.toLowerCase();
      this.textToSearch = searchText;

      if (settingType === 'statusList') {
        // Filter status list - create filtered copy without modifying backup
        const filtered = _.filter(this.statusListBackup, (status: Status) => {
          return status.name.toLowerCase().indexOf(searchText) >= 0;
        });
        this.separateStatusBasedOnSequence(filtered);
      } else if (settingType === 'statusRules') {
        // Filter status rules
        const filtered = _.filter(this.statusRulesBackup, (rule: Rule) => {
          return rule.name.toLowerCase().indexOf(searchText) >= 0;
        });
        this.filteredStatusRules = _.cloneDeep(filtered);
        this.getRulePageWise();
      }
    } else {
      if (settingType === 'statusList') {
        // Reset to full list
        this.separateStatusBasedOnSequence(_.cloneDeep(this.statusListBackup));
      } else {
        this.getRulePageWise();
      }
    }
  }

  /**
   * Open modal for status or rule
   */
  openModal(prepopulateData?: any): void {
    const editMode:string = prepopulateData ? 'Update ' : 'New ';
    const data = !_.isEmpty(prepopulateData) ? prepopulateData : {};
    if(!prepopulateData || _.isEmpty(prepopulateData)){
      if(this.readonly){
        Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
        return
      }
    }
    if (this.settings.selected === 'basic') {
      this.openStatusModal(data);
    } else {
      this.openRuleModal(data);
    }
  }

  /**
   * Open status modal
   * Matches old AngularJS logic: populate all fields from prepopulateData
   */
  private openStatusModal(prepopulateData: any): void {
    // Ensure prepopulateData exists
    if (!prepopulateData) {
      prepopulateData = {};
    }

    // If editing, get the full status object from backup to ensure we have all fields
    if (prepopulateData && prepopulateData.uuid) {
      const fullStatus = _.find(this.statusListBackup, (status: Status) => status.uuid === prepopulateData.uuid);
      if (fullStatus) {
        // Merge full status data with prepopulateData, but don't overwrite with undefined values
        // This ensures we have all fields from the backup, but preserve any explicitly set values
        const mergedData: any = { ...fullStatus };
        Object.keys(prepopulateData).forEach(key => {
          if (prepopulateData[key] !== undefined && prepopulateData[key] !== null) {
            mergedData[key] = prepopulateData[key];
          }
        });
        prepopulateData = mergedData;
      }
    }

    // Extract levels from access_key_rules if present (matching old AngularJS behavior)
    // Old code: enabledLevels = Utils.getProp(prepopulateData, "access_key_rules.0.levels") || [];
    if (prepopulateData.access_key_rules && prepopulateData.access_key_rules[0]?.levels) {
      prepopulateData.levels = Array.isArray(prepopulateData.access_key_rules[0].levels)
        ? [...prepopulateData.access_key_rules[0].levels]
        : [];
    } else if (!prepopulateData.levels) {
      prepopulateData.levels = [];
    }

    // Prepare access levels
    const accessLevels = this.getAccessLevels(prepopulateData);

    // Prepare status options for modal
    const statusOptions = _.cloneDeep(this.statusModalSchema);
    statusOptions.title = prepopulateData.uuid ? 'Edit Status' : 'Add Status';

    // Update levels property with table rows
    // if (statusOptions.properties.levels) {
    //   statusOptions.properties.levels.tableRows = accessLevels;
    // }

    // Check if this is a new status (no uuid) or editing existing status
    const isNewStatus = !prepopulateData || !prepopulateData.uuid;

    // Convert accessLevels array to object format for checkbox-table
    // Matching old AngularJS: is_enabled is set in getAccessLevels based on enabledLevelsArray
    // Use the isEnabled property from getAccessLevels which already checks access_key_rules[0].levels
    // const levelsObject: any = {};
    // accessLevels.forEach((row: any) => {
    //   // For new status: set all to true, for editing: use the isEnabled value from getAccessLevels
    //   // getAccessLevels already checks if the key is in access_key_rules[0].levels
    //   levelsObject[row.key] = isNewStatus ? true : (row.isEnabled === true);
    // });

    // Normalize trigger_for to array format
    // Ensure it matches the option IDs: 'parent', 'siblings', 'children'
    let triggerForArray: string[] = [];
    if (prepopulateData.trigger_for) {
      if (Array.isArray(prepopulateData.trigger_for)) {
        // Filter out any invalid values and ensure they match option IDs
        triggerForArray = prepopulateData.trigger_for.filter((val: any) =>
          val && ['parent', 'siblings', 'children'].includes(val)
        );
      } else if (typeof prepopulateData.trigger_for === 'string') {
        // Single string value
        if (['parent', 'siblings', 'children'].includes(prepopulateData.trigger_for)) {
          triggerForArray = [prepopulateData.trigger_for];
        }
      }
    }

    // Set default values - populate ALL saved fields from prepopulateData
    // Matching old AngularJS: directly assign values from prepopulateData (lines 423-429)
    // Use explicit checks for boolean fields to preserve false values
    const defaultValues: any = {
      uuid: prepopulateData.uuid || '',
      name: prepopulateData.name || '',
      color: prepopulateData.color || '#c09853',
      // Use !== undefined check to preserve false values (matching old AngularJS behavior)
      // Convert to boolean explicitly to ensure proper type
      out_of_sequence: prepopulateData.out_of_sequence !== undefined ? Boolean(prepopulateData.out_of_sequence) : false,
      exclude_from_ageing: prepopulateData.exclude_from_ageing !== undefined ? Boolean(prepopulateData.exclude_from_ageing) : false,
      // Handle trigger_for array properly - ensure it's always an array with valid option IDs
      trigger_for: triggerForArray,
      hide_slaTimer_for_status: prepopulateData.hide_slaTimer_for_status !== undefined ? Boolean(prepopulateData.hide_slaTimer_for_status) : false,
      levels: accessLevels // Object format for checkbox-table: { 'overall': true, 'product': false, ... }
    };


    this.modalSchema = statusOptions;
    this.defaultValues = defaultValues;
    // Store deep clone of original data for change detection
    // Preserve levels in selectedStatusDetails for comparison
    this.selectedStatusDetails = _.cloneDeep(prepopulateData);
    if (this.selectedStatusDetails && this.selectedStatusDetails.access_key_rules && this.selectedStatusDetails.access_key_rules[0]?.levels) {
      this.selectedStatusDetails.levels = Array.isArray(this.selectedStatusDetails.access_key_rules[0].levels)
        ? [...this.selectedStatusDetails.access_key_rules[0].levels]
        : [];
    }
    this.isModalOpen = true;
  }

  /**
   * Open rule modal
   */
  private openRuleModal(prepopulateData?: any): void {
    // Get status options for multi-select
    const statusOptions = this.statusListBackup.map((status: Status) => ({
      uuid: status.uuid,
      name: status.name,
      label: status.name,
      value: status.uuid
    }));

    // Fetch workflow rules for conditions
    this.getWorkflowRules().then(() => {
      this.initializeRuleModal(statusOptions, prepopulateData);
    });
    // this._settingsService.getWorkflowRules(this.brandId, 3)
    //   .pipe(takeUntilDestroyed(this.destroyRef))
    //   .subscribe({
    //     next: (response: any) => {

    //       // Extract fields and operators from all rulesets
    //       this.workflowFields = [];
    //       this.workflowOperators = [];

    //       // Handle different possible response structures
    //       let rulesets: any[] = [];
    //       if (response && response.data) {
    //         if (Array.isArray(response.data)) {
    //           rulesets = response.data;
    //         } else if (response.data.data && Array.isArray(response.data.data)) {
    //           rulesets = response.data.data;
    //         } else if (response.data.rulesets && Array.isArray(response.data.rulesets)) {
    //           rulesets = response.data.rulesets;
    //         }
    //       }

    //       // Process each ruleset to extract f ields and operators
    //       rulesets.forEach((ruleset: any, index: number) => {
    //         if (ruleset._id && !this.ruleSetIdsToExclude.includes(ruleset._id)) {
    //           if (ruleset && ruleset.schema) {
    //             // Extract fields from schema.options (object structure)
    //             if (ruleset.schema.options && typeof ruleset.schema.options === 'object') {
    //               const optionsObj = ruleset.schema.options;
    //               const isArray = Array.isArray(optionsObj);
    //               const optionsToProcess = isArray ? optionsObj : Object.values(optionsObj);

    //               optionsToProcess.forEach((field: any) => {
    //                 if (!field || typeof field !== 'object') return;

    //                 const fieldValue = field.unique_id || field.field_id || field.id || field._id;
    //                 const fieldLabel = field.display_name || field.name || field.label || fieldValue;

    //                 if (fieldValue) {
    //                   const fieldObj = {
    //                     label: fieldLabel || 'Unnamed Field',
    //                     value: fieldValue,
    //                     field_id: field.field_id,
    //                     unique_id: field.unique_id,
    //                     value_type: field.value_type,
    //                     originalField: field
    //                   };

    //                   const exists = this.workflowFields.some((f: any) =>
    //                     f.value === fieldObj.value ||
    //                     (fieldObj.unique_id && f.unique_id === fieldObj.unique_id) ||
    //                     (fieldObj.field_id && f.field_id === fieldObj.field_id)
    //                   );

    //                   if (!exists) {
    //                     this.workflowFields.push(fieldObj);
    //                   }
    //                 }
    //               });
    //             }

    //             // Extract operators from schema.operators
    //             if (ruleset.schema.operators) {
    //               let operatorsToProcess: any[] = [];

    //               if (Array.isArray(ruleset.schema.operators)) {
    //                 operatorsToProcess = ruleset.schema.operators;
    //               } else if (typeof ruleset.schema.operators === 'object') {
    //                 const operatorValues = Object.values(ruleset.schema.operators);
    //                 operatorsToProcess = operatorValues.flatMap((op: any) => {
    //                   if (Array.isArray(op)) {
    //                     return op;
    //                   } else if (op && typeof op === 'object') {
    //                     return [op];
    //                   }
    //                   return [];
    //                 });
    //               }

    //               operatorsToProcess.forEach((operator: any) => {
    //                 let opObj: any = null;

    //                 if (typeof operator === 'object' && operator !== null) {
    //                   const opValue = operator._id || operator.id || operator.value || operator.operator_id;
    //                   const opLabel = operator.label || operator.name || operator.display_name || operator.text || opValue;

    //                   if (opValue) {
    //                     opObj = {
    //                       label: opLabel,
    //                       value: opValue,
    //                       originalOperator: operator
    //                     };
    //                   }
    //                 } else if (typeof operator === 'string') {
    //                   const operatorMap: any = {
    //                     '$eq': 'Equals',
    //                     '$ne': 'Not Equals',
    //                     '$lt': 'Less Than',
    //                     '$lte': 'Less Than or Equal',
    //                     '$gt': 'Greater Than',
    //                     '$gte': 'Greater Than or Equal',
    //                     '$in': 'Contains',
    //                     '$has': 'Contains'
    //                   };
    //                   opObj = {
    //                     label: operatorMap[operator] || operator,
    //                     value: operator
    //                   };
    //                 }

    //                 if (opObj && opObj.value) {
    //                   const exists = this.workflowOperators.some((op: any) => op.value === opObj.value);
    //                   if (!exists) {
    //                     this.workflowOperators.push(opObj);
    //                   }
    //                 }
    //               });
    //             }
    //           }
    //         }
    //       });

    //       // Fallback if no fields/operators found
    //       if (this.workflowFields.length === 0) {
    //         this.workflowFields = [
    //           { label: 'Status ID List', value: 'status_id_list' },
    //           { label: 'Category', value: 'category' },
    //           { label: 'Primary Rating', value: 'feed_params.primary_rating' },
    //           { label: 'Tags', value: 'tags' }
    //         ];
    //       }

    //       if (this.workflowOperators.length === 0) {
    //         this.workflowOperators = [
    //           { label: 'Equals', value: '$eq' },
    //           { label: 'Not Equals', value: '$ne' },
    //           { label: 'Less Than', value: '$lt' },
    //           { label: 'Less Than or Equal', value: '$lte' },
    //           { label: 'Greater Than', value: '$gt' },
    //           { label: 'Greater Than or Equal', value: '$gte' },
    //           { label: 'Contains', value: '$in' }
    //         ];
    //       }

    //       // Initialize rule modal
    //       this.initializeRuleModal(statusOptions, prepopulateData);
    //     },
    //     error: (error: any) => {
    //       console.error('Error fetching workflow rules:', error);
    //       // Use fallback
    //       this.workflowFields = [
    //         { label: 'Status ID List', value: 'status_id_list' },
    //         { label: 'Category', value: 'category' },
    //         { label: 'Primary Rating', value: 'feed_params.primary_rating' },
    //         { label: 'Tags', value: 'tags' }
    //       ];
    //       this.workflowOperators = [
    //         { label: 'Equals', value: '$eq' },
    //         { label: 'Not Equals', value: '$ne' },
    //         { label: 'Less Than', value: '$lt' },
    //         { label: 'Less Than or Equal', value: '$lte' },
    //         { label: 'Greater Than', value: '$gt' },
    //         { label: 'Greater Than or Equal', value: '$gte' },
    //         { label: 'Contains', value: '$in' }
    //       ];
    //       this.initializeRuleModal(statusOptions, prepopulateData);
    //     }
    //   });
  }

  rulesetHash: any = {}
  getWorkflowRules() {
    return new Promise((resolve) => {
      // Fetch workflow rules (fields and operators) from API
      this._settingsService.getWorkflowRules(this.brandId, 3)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response: any) => {

            // Extract fields and operators from all rulesets
            this.workflowFields = [];
            this.workflowOperators = [];

            // Handle different possible response structures
            let rulesets: any[] = [];
            if (response && response.data) {
              if (Array.isArray(response.data)) {
                rulesets = response.data;
              } else if (response.data.data && Array.isArray(response.data.data)) {
                rulesets = response.data.data;
              } else if (response.data.rulesets && Array.isArray(response.data.rulesets)) {
                rulesets = response.data.rulesets;
              }
            }

            if (this.ruleSetIdsToExclude && this.ruleSetIdsToExclude.length) {
              rulesets = _.filter(rulesets, (ruleSet: any) => {
                if (!(ruleSet._id && this.ruleSetIdsToExclude.indexOf(ruleSet._id) >= 0)) {
                  return ruleSet;
                }
              })
            }


            rulesets = _.map(rulesets, (condition: any) => {
              if (condition._id == "response_fields_logic_ruleset") {
                condition = this.getExternalFields(condition);
              }
              // add property id and text as this will be used in select2
              condition["id"] = condition["_id"];
              condition["text"] = condition["name"];

              // update workflowConditionsVM.rulesetHash
              this.rulesetHash[condition["_id"]] = condition;
              return condition;
            });

            _.each(rulesets, (rule: any) => {
              var options: any = _.get(rule, "schema.options");
              var operators: any = _.get(rule, "schema.operators");
              const rulesetId = rule["_id"];
              if (!_.isEmpty(options)) {
                _.each(options, (fieldDetail: any, key: string) => {
                  const valueType = _.get(fieldDetail, 'value_type')
                  if ((!_.hasIn(fieldDetail.operators) || _.isEmpty(fieldDetail.operators)) && !_.isEmpty(operators) && _.has(operators, valueType) && !_.isEmpty(operators[valueType])) {
                    fieldDetail.operators = _.cloneDeep(operators[valueType])
                  }
                  // fieldDetail['id'] = fieldDetail['field_id'];
                  fieldDetail['id'] = fieldDetail['unique_id'];
                  fieldDetail['text'] = fieldDetail['display_name'];
                  fieldDetail['ruleset_id'] = rulesetId;
                  fieldDetail['ruleset_key'] = key;
                  this.workflowFields.push(fieldDetail);
                })
              }

            });

            // // If no operators found, use default operators
            if (this.workflowOperators.length === 0) {
              console.warn('No operators found in API response, using fallback');
              this.workflowOperators = [
                { label: 'Equals', value: '$eq' },
                { label: 'Not Equals', value: '$ne' },
                { label: 'Less Than', value: '$lt' },
                { label: 'Less Than or Equal', value: '$lte' },
                { label: 'Greater Than', value: '$gt' },
                { label: 'Greater Than or Equal', value: '$gte' },
                { label: 'Contains', value: '$in' }
              ];
            }


            resolve({
              workflowFields: this.workflowFields,
              workflowOperators: this.workflowOperators
            })
          },
          error: (error: any) => {
            console.error('Error fetching workflow rules:', error);
            // Fallback to default values if API fails
            this.workflowFields = [
              { label: 'Status ID List', value: 'status_id_list' },
              { label: 'Category', value: 'category' },
              { label: 'Primary Rating', value: 'feed_params.primary_rating' },
              { label: 'Tags', value: 'tags' }
            ];
            this.workflowOperators = [
              { label: 'Equals', value: '$eq' },
              { label: 'Not Equals', value: '$ne' },
              { label: 'Less Than', value: '$lt' },
              { label: 'Less Than or Equal', value: '$lte' },
              { label: 'Greater Than', value: '$gt' },
              { label: 'Greater Than or Equal', value: '$gte' },
              { label: 'Contains', value: '$in' }
            ];
            resolve({
              workflowFields: this.workflowFields,
              workflowOperators: this.workflowOperators
            })
          }
        });
    })

  }

  getExternalFields(ruleSet) {
    var addedItemsMap = {}
    if (ruleSet && ruleSet.schema && !(_.isEmpty(ruleSet.schema.options))) {
      for (var field_ in this.fieldDefsMap) {
        var fieldItem = this.fieldDefsMap[field_]
        if (fieldItem.custom_parameter_type == 4) {
          if (fieldItem.group_id && !(addedItemsMap[fieldItem.group_id])) {
            var groupField = this.fieldDefsMap[fieldItem.group_id];
            if (groupField && !groupField.group_id) {
              addedItemsMap[fieldItem.group_id] = 1;
              this.setRuleSetObj(this.fieldDefsMap[fieldItem.group_id], ruleSet)
            }
          }
          if (fieldItem.data_type == "date") {
            this.setRuleSetObj(this.fieldDefsMap[fieldItem.field_id], ruleSet, (fieldItem["display_name"] || fieldItem["name"]))
          }
        }

      }
    }
    return ruleSet;
  }


  /* By Bhunesh - This function prepares dynamic ruleset object for all the custom accordions and fields(date fields)*
                  to be moved to backend
          */
  setRuleSetObj(fieldItem: any, ruleSet: any, textToPrepend?: any) {
    if (ruleSet && fieldItem) {
      var group = this.fieldDefsMap[fieldItem.group_id];
      ["Business", ""].forEach(function (ageType) {
        ["Hours", "Minutes"].forEach(function (timeType) {
          var field_id: any = null;
          var name: any = null;
          var sep: any = (ageType == "Business") ? " " : "";
          var ruleSetObjectPath: any;
          if (textToPrepend) {
            var groupname = "";
            if (group && (group.name || group.display_name)) {
              groupname = group.display_name || group.name;
            }
            field_id = "response__has_" + fieldItem.field_id + "_diff_" + ageType.toLowerCase() + "_" + timeType.toLowerCase();
            name = groupname + " - " + textToPrepend + ' (' + ageType.trim() + sep + timeType + ')';
            ruleSetObjectPath = "response.litmus_processed_data." + fieldItem["field_id"];
          } else {
            field_id = "response__has_" + fieldItem.field_id + "_last_updated_" + ageType.toLowerCase() + "_" + timeType.toLowerCase();
            name = "Time Since Last " + (fieldItem["display_name"] || fieldItem["name"]) + " Update (" + ageType.trim() + sep + timeType + ")";
            ruleSetObjectPath = "response.update_logs." + fieldItem["field_id"] + ".last_updated_on";
          }
          ruleSet.schema.options[field_id] = {
            "name": name,
            "should_evaluate": false,
            "field_id": ruleSetObjectPath,
            "display_name": name,
            "value_type": "date",
            "unique_id": "response_fields_logic_ruleset___" + field_id
          }
        })
      })
    }
  }

  /**
   * Initialize rule modal
   */
  private initializeRuleModal(statusOptions: any[], prepopulateData?: any): void {
    let selectedStatuses: any = [];
    const dataToPreprocess = _.cloneDeep(prepopulateData);
    if (dataToPreprocess?.status_list && Array.isArray(dataToPreprocess.status_list)) {
      dataToPreprocess.status_list.forEach((statusId: any) => {
        const statusDetail: any = this.statusListBackup.find((status: Status) => status.uuid === statusId);
        if (statusDetail) {
          selectedStatuses.push({
            name: statusDetail.name,
            uuid: statusDetail.uuid,
            label: statusDetail.name,
            value: statusDetail.uuid
          })
        }
        return null;
      });
    }
    const ruleDefaultValues: any = {
      id: dataToPreprocess?.id || '',
      name: dataToPreprocess?.name || '',
      type: 'visibility',
      status_list: selectedStatuses || [],
      description: dataToPreprocess?.description || '',
      conditions: dataToPreprocess?.conditions || []
    };
    const defaultObject = _.cloneDeep(statusRuleSchema.conditions.properties.dataObject)
    defaultObject.editing = true;
    ruleDefaultValues.conditions.push(defaultObject);

    const modalSchemaCopy = _.cloneDeep(this.ruleModalSchema);
    modalSchemaCopy.title = prepopulateData?.id ? 'Edit Rule' : 'Add Rule';
    modalSchemaCopy.basic.properties.status_list.options = statusOptions;

    // Update conditions field options with API data
    if (!_.hasIn(modalSchemaCopy, 'conditions.properties')) {
      modalSchemaCopy.conditions.properties = {}
    }
    modalSchemaCopy.conditions.properties.rulesetlist = [...this.workflowFields]
    modalSchemaCopy.conditions.properties.rulesetHash = { ...this.rulesetHash }
    modalSchemaCopy.conditions.properties.fieldDefsMap = { ...this.fieldDefsMap }

    this.modalSchema = modalSchemaCopy;
    this.defaultValues = ruleDefaultValues;
    this.selectedRuleDetails = prepopulateData || null;
    this.isModalOpen = true;
  }

  prepareHierarchyMapping() {

    const hierarchyList = this.actionService.prepareViewTypeDropdown(this.brandDetails)
    let unformattedMapping = hierarchyList?.hierarchyMapping

    // Get all keys from the object
    const keys = Object.keys(unformattedMapping);

    // Get the last key
    const lastKey = keys[keys.length - 1];

    // Check if the value is undefined and update it
    if (unformattedMapping[lastKey] === undefined) {
      unformattedMapping[lastKey] = "Touchpoint";
    }

    this.hierarchyMapping = unformattedMapping;
    this.prepareTableRowForCategoryModal()
  }

  prepareTableRowForCategoryModal() {
    let tableRows: any[] = [];
    const keys = Object.keys(this.hierarchyMapping);

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const label = this.hierarchyMapping[key]!;

      tableRows.push({
        label,
        key,
        default: true // mark first one as default
      });
    }
    this.tableRows = tableRows
    // this.statusModalSchema.properties.levels.tableRows = tableRows
  }

  /**
   * Get access levels for status modal
   * Matches old AngularJS getAccessLevels logic (lines 574-599)
   */
  private getAccessLevels(prepopulateData: any): any[] {
    // Ensure hierarchy mapping is prepared
    if (_.isEmpty(this.hierarchyMapping)) {
      this.prepareHierarchyMapping();
    }

    // Get enabled levels from prepopulateData.access_key_rules[0].levels
    // Matching old AngularJS: enabledLevels = Utils.getProp(prepopulateData, "access_key_rules.0.levels") || [];
    const enabledLevels = prepopulateData.access_key_rules && prepopulateData.access_key_rules[0]?.levels
      ? [...prepopulateData.access_key_rules[0].levels]
      : (prepopulateData.levels ? [...prepopulateData.levels] : []);

    // Check if this is a new status (no uuid) or editing existing status
    const isNewStatus = !prepopulateData || !prepopulateData.uuid;

    // Use the prepared tableRows and update default values based on enabled levels
    const keys = Object.keys(this.hierarchyMapping);
    let tableRows: any[] = [];

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const label = this.hierarchyMapping[key]!;

      // Matching old AngularJS: is_enabled: (enabledLevels && (enabledLevels).indexOf(value) > -1) ? true : false
      // For new status: all levels enabled by default
      // For editing: check if the key exists in enabledLevels array
      const isEnabled = isNewStatus ? true :
        (Array.isArray(enabledLevels) && enabledLevels.length > 0 && enabledLevels.indexOf(key) > -1);

      tableRows.push({
        label,
        key,
        visible: isEnabled
      });
    }

    return tableRows;
  }

  /**
   * Edit status
   */
  editStatus(status: Status): void {
    this.openModal(status);
  }

  /**
   * Delete status
   */
  deleteStatus(status: Status): void {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    if (Helpers && Helpers.showConfirmationBox) {
      Helpers.showConfirmationBox('Confirmation', 'Are you sure you want to delete this status?', (answer: boolean) => {
        if (answer) {
          status.is_deleted = true;
          this.updateStatus(status, 'delete');
        }
      });
    } else {
      const confirmed = confirm('Are you sure you want to delete this status?');
      if (confirmed) {
        status.is_deleted = true;
        this.updateStatus(status, 'delete');
      }
    }
  }

  /**
   * Edit rule
   */
  editRule(rule: Rule): void {
    this.openModal(rule);
  }

  /**
   * Delete rule
   */
  deleteRule(rule: Rule): void {
    if(this.readonly){
      Helpers.smallAlertBox('Access denied',"You do not have write permission","info",1500)
      return
    }
    if (Helpers && Helpers.showConfirmationBox) {
      Helpers.showConfirmationBox('Confirmation', 'Are you sure you want to delete this rule?', (answer: boolean) => {
        if (answer) {
          rule.is_deleted = true;
          this.updateRule(rule);
        }
      });
    } else {
      const confirmed = confirm('Are you sure you want to delete this rule?');
      if (confirmed) {
        rule.is_deleted = true;
        this.updateRule(rule);
      }
    }
  }

  /**
   * Enable/disable rule
   */
  enableRule(rule: Rule): void {
    this.updateRule(rule, true);
  }

  /**
   * Submit form (status or rule)
   */
  submitForm(data: any): void {
    if (this.settings.selected === 'basic') {
      this.submitStatusForm(data);
    } else {
      this.submitRuleForm(data);
    }
  }

  /**
   * Submit status form
   */
  private submitStatusForm(data: any): void {
    // Check for duplicate status name
    const statusWithSameName = _.filter(this.statusListBackup, (status: Status) => {
      return status.uuid !== data.uuid && status.name.toLowerCase().trim() === data.name.toLowerCase().trim();
    });

    if (statusWithSameName && statusWithSameName.length) {
      if (Helpers && Helpers.smallAlertBox) {
        Helpers.smallAlertBox('Warning', 'Status name already exists, please enter another name.', 'warn');
      }
      return;
    }

    // Check for duplicate color
    const existingStatusWithColor = _.filter(this.statusListBackup, (status: Status) => {
      return status.color === data.color && status.uuid !== data.uuid;
    });

    if (existingStatusWithColor && existingStatusWithColor.length) {
      if (Helpers && Helpers.smallAlertBox) {
        Helpers.smallAlertBox('Warning', 'Status color already exists, please select another color.', 'warn');
      }
      return;
    }

    if (data.uuid) {
      // Update existing status - check if any changes were made
      const hasChanges = this.hasStatusChanges(data);

      if (!hasChanges) {
        // No changes detected
        if (Helpers && Helpers.smallAlertBox) {
          Helpers.smallAlertBox('Warning', 'No changes made.', 'warn');
        }
        this.closeModal();
        return;
      }

      // Changes detected - proceed with update
      if (Helpers && Helpers.showConfirmationBox) {
        Helpers.showConfirmationBox('Confirmation', 'Please note that this change will Override the previous configuration. Are you sure, you want to proceed?', (answer: boolean) => {
          if (answer) {
            // Preserve sequence from existing status (matching old AngularJS behavior)
            const statusDetail = _.find(this.statusListBackup, (status: Status) => status.uuid === data.uuid);
            if (statusDetail) {
              data.sequence = statusDetail.sequence;
            }
            this.updateStatus(data);
          }
        });
      } else {
        // Preserve sequence from existing status (matching old AngularJS behavior)
        const statusDetail = _.find(this.statusListBackup, (status: Status) => status.uuid === data.uuid);
        if (statusDetail) {
          data.sequence = statusDetail.sequence;
        }
        this.updateStatus(data);
      }
    } else {
      // Add new status
      this.updateStatus(data);
    }
  }

  /**
   * Check if status data has changed from the original saved configuration
   */
  private hasStatusChanges(formData: any): boolean {
    if (!this.selectedStatusDetails || !formData.uuid) {
      // New status - always has changes (or no original to compare)
      return true;
    }

    const original = this.selectedStatusDetails;

    // Compare basic fields (with null/undefined handling)
    const formName = (formData.name || '').trim();
    const originalName = (original.name || '').trim();
    if (formName !== originalName) {
      return true;
    }

    const formColor = formData.color || '';
    const originalColor = original.color || '';
    if (formColor !== originalColor) {
      return true;
    }

    const formOutOfSequence = !!formData.out_of_sequence;
    const originalOutOfSequence = !!original.out_of_sequence;
    if (formOutOfSequence !== originalOutOfSequence) {
      return true;
    }

    const formExcludeFromAgeing = !!formData.exclude_from_ageing;
    const originalExcludeFromAgeing = !!original.exclude_from_ageing;
    if (formExcludeFromAgeing !== originalExcludeFromAgeing) {
      return true;
    }

    const formHideSlaTimer = !!formData.hide_slaTimer_for_status;
    const originalHideSlaTimer = !!original.hide_slaTimer_for_status;
    if (formHideSlaTimer !== originalHideSlaTimer) {
      return true;
    }

    // Compare trigger_for arrays (normalize and sort for comparison)
    const formTriggerFor = Array.isArray(formData.trigger_for)
      ? [...formData.trigger_for].filter(t => t).sort()
      : [];
    const originalTriggerFor = Array.isArray(original.trigger_for)
      ? [...original.trigger_for].filter(t => t).sort()
      : [];
    if (formTriggerFor.length !== originalTriggerFor.length ||
      !_.isEqual(formTriggerFor, originalTriggerFor)) {
      return true;
    }

    // Compare levels (convert form data levels object to array for comparison)
    const formLevelsArray = this.convertLevelsObjectToArray(formData.levels);
    // Get original levels from access_key_rules or levels field
    let originalLevelsArray: string[] = [];
    if (original.access_key_rules && original.access_key_rules[0]?.levels) {
      originalLevelsArray = Array.isArray(original.access_key_rules[0].levels)
        ? original.access_key_rules[0].levels
        : [];
    } else if (original.levels) {
      originalLevelsArray = this.convertLevelsObjectToArray(original.levels);
    }

    // Sort arrays for comparison (normalize empty arrays)
    const formLevelsSorted = formLevelsArray.length > 0 ? [...formLevelsArray].sort() : [];
    const originalLevelsSorted = originalLevelsArray.length > 0 ? [...originalLevelsArray].sort() : [];

    if (formLevelsSorted.length !== originalLevelsSorted.length ||
      !_.isEqual(formLevelsSorted, originalLevelsSorted)) {
      return true;
    }

    // No changes detected
    return false;
  }

  /**
   * Convert levels object (from checkbox-table) to array format
   */
  private convertLevelsObjectToArray(levels: any): string[] {
    if (!levels) {
      return [];
    }

    if (Array.isArray(levels)) {
      return levels;
    }

    if (typeof levels === 'object') {
      // Convert object format { 'overall': true, 'product': false, ... } to array ['overall', ...]
      const levelsObj = levels as Record<string, boolean>;
      return Object.keys(levelsObj).filter(key => levelsObj[key] === true);
    }

    return [];
  }

  /**
   * Submit rule form
   */
  private submitRuleForm(data: any): void {
    // Check for duplicate rule name
    const ruleWithSameName = _.filter(this.statusRulesBackup, (rule: Rule) => {
      return rule.id !== data.id && rule.name.toLowerCase().trim() === data.name.toLowerCase().trim();
    });

    if (ruleWithSameName && ruleWithSameName.length) {
      if (Helpers && Helpers.smallAlertBox) {
        Helpers.smallAlertBox('Warning', 'Rule name already exists, please enter another name', 'warn');
      }
      return;
    }

    if (data.id && data.id !== "") {
      // Update existing rule
      Helpers.showConfirmationBox('Confirmation', 'Please note that this change will Override the previous configuration. Are you sure, you want to proceed?', (answer: boolean) => {
        if (answer) {
          this.updateRule(data);
        }
      });
    } else {
      // Add new rule
      this.updateRule(data);
    }
  }

  /**
   * Update status
   * Matches the request payload structure from old AngularJS component
   */
  private updateStatus(statusDetail: Status, type?: string): void {
    // Convert levels object (from checkbox-table) to array format for API
    let levelsArray: string[] = [];
    if (statusDetail.levels) {
      if (typeof statusDetail.levels === 'object' && !Array.isArray(statusDetail.levels)) {
        // Convert object format { 'overall': true, 'product': false, ... } to array ['overall', ...]
        const levelsObj = statusDetail.levels as Record<string, boolean>;
        levelsArray = Object.keys(levelsObj).filter(key => levelsObj[key] === true);
      } else if (Array.isArray(statusDetail.levels)) {
        // Already an array
        levelsArray = statusDetail.levels;
      }
    }

    // Prepare access_key_rules from levels array (matching old AngularJS structure)
    if (levelsArray && levelsArray.length > 0) {
      const access_key_rules = { levels: _.cloneDeep(levelsArray), type: 'visibility' };
      delete statusDetail.levels;
      statusDetail.access_key_rules = [access_key_rules];
    } else {
      statusDetail.access_key_rules = [{ levels: [], type: 'visibility' }];
    }

    // Set required fields (matching old AngularJS component)
    statusDetail.visible = true;
    statusDetail.is_enabled = true;

    // Ensure trigger_for is an array (matching old AngularJS component)
    if (statusDetail.trigger_for && !Array.isArray(statusDetail.trigger_for)) {
      statusDetail.trigger_for = [statusDetail.trigger_for];
    }

    this.callServiceToUpdateStatus(statusDetail, type);
  }

  /**
   * Call service to update status
   */
  private callServiceToUpdateStatus(statusDetail: Status, type?: string): void {
    if (statusDetail.uuid) {
      // Update existing status
      let isOutOfSeq = false;
      let statusSeq: number | null = null;

      if (statusDetail.out_of_sequence) {
        statusSeq = statusDetail.sequence;
        statusDetail.sequence = null;
        isOutOfSeq = true;
      } else if (!statusDetail.sequence && this.statusList.sequenced) {
        statusDetail.sequence = this.statusList.sequenced.length + 1;
      } else {
        statusDetail.sequence = statusDetail.sequence || 1;
      }

      // Handle sequence updates for out of sequence statuses
      let statusWithSeqGrtThanOrEql: Status[] = [];
      if (isOutOfSeq && statusSeq) {
        statusWithSeqGrtThanOrEql = _.filter(this.statusListBackup, (status: Status) => {
          return status.sequence && status.sequence > statusSeq!;
        });

        _.each(statusWithSeqGrtThanOrEql, (status: Status) => {
          if (status.sequence) status.sequence--;
        });
      }

      // Handle deletion sequence updates
      let sequenceUpdatedList: Status[] = [];
      if (statusDetail.is_deleted && statusDetail.sequence) {
        sequenceUpdatedList = _.filter(this.statusListBackup, (status: Status) => {
          if (status.sequence && status.sequence > statusDetail.sequence!) {
            status.sequence = status.sequence - 1;
          }
          return status;
        });
      }

      if (statusWithSeqGrtThanOrEql && statusWithSeqGrtThanOrEql.length) {
        statusWithSeqGrtThanOrEql.push(statusDetail);
        this.updateStatuses('updat', statusWithSeqGrtThanOrEql);
      } else if (sequenceUpdatedList && sequenceUpdatedList.length) {
        this.updateStatuses('delete', sequenceUpdatedList, false);
      } else {
        // Update single status
        this._settingsService.updateStatus(this.brandId, statusDetail)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (response: any) => {
              if (response && response.data) {
                if (response.data.code === 0) {
                  this.resetAndCloseModal();
                  const message = type === 'delete' ? 'Status deleted successfully.' : 'Status updated successfully.';
                  if (Helpers && Helpers.smallAlertBox) {
                    Helpers.smallAlertBox('Success', message, 'Success');
                  }
                } else if (response.data.code === 4021) {
                  if (Helpers && Helpers.smallAlertBox) {
                    Helpers.smallAlertBox('Error', 'Status already exists.', 'error');
                  }
                } else if (response.data.code === 4022) {
                  if (Helpers && Helpers.smallAlertBox) {
                    Helpers.smallAlertBox('Warning', 'No changes made.', 'warn');
                  }
                } else {
                  if (Helpers && Helpers.smallAlertBox) {
                    Helpers.smallAlertBox('Error', 'Problem while updating status name.', 'error');
                  }
                }
              }
            },
            error: (error: any) => {
              console.error('Error updating status:', error);
              if (Helpers && Helpers.smallAlertBox) {
                Helpers.smallAlertBox('Error', 'Problem while updating status name.', 'error');
              }
            }
          });
      }
    } else {
      // Add new status
      if (!statusDetail.out_of_sequence) {
        statusDetail.sequence = this.statusList.sequenced ? (this.statusList.sequenced.length + 1) : 1;
      } else {
        statusDetail.sequence = null;
      }

      this._settingsService.addStatusByBrandId(this.brandId, [statusDetail])
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response: any) => {
            if (response && response.data) {
              if (response.data.code === 0) {
                if (response.data.status_id) {
                  statusDetail.uuid = response.data.status_id;
                }
                this.statusListBackup.push(statusDetail);
                this.resetAndCloseModal();
                if (Helpers && Helpers.smallAlertBox) {
                  Helpers.smallAlertBox('Success', 'Status created successfully.', 'Success');
                }
              } else if (response.data.code === 4021) {
                if (Helpers && Helpers.smallAlertBox) {
                  Helpers.smallAlertBox('Warning', 'Status name already exists, please enter another name.', 'warn');
                }
              } else {
                if (Helpers && Helpers.smallAlertBox) {
                  Helpers.smallAlertBox('Error', 'Problem while adding Status.', 'error');
                }
              }
            }
          },
          error: (error: any) => {
            console.error('Error adding status:', error);
            if (Helpers && Helpers.smallAlertBox) {
              Helpers.smallAlertBox('Error', 'Problem while adding Status.', 'error');
            }
          }
        });
    }
  }

  /**
   * Update multiple statuses
   */
  private updateStatuses(type: string, statusesWithRule: Status[], isStatusSequenceUpdate?: boolean): void {
    this._settingsService.updateRuleBasedOnType(1, this.brandId, statusesWithRule)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          if (response && response.data) {
            if (response.data.code === 0) {
              this.resetAndCloseModal();
              let message = '';
              if (isStatusSequenceUpdate) {
                message = 'Status updated successfully.';
              } else if (type === 'delete') {
                message = 'Status deleted successfully.';
              } else if (type === 'add') {
                message = 'Rule saved successfully, please activate from the list.';
              } else {
                message = 'Status ' + type + 'ed successfully.';
              }
              if (Helpers && Helpers.smallAlertBox) {
                Helpers.smallAlertBox('Success', message, 'Success');
              }
            } else if (response.data.code === 4022) {
              this.pagerBusy = false;
              if (Helpers && Helpers.smallAlertBox) {
                Helpers.smallAlertBox('Error', 'No changes made', 'warn');
              }
            } else {
              this.pagerBusy = false;
              if (Helpers && Helpers.smallAlertBox) {
                Helpers.smallAlertBox('Error', 'Problem while ' + type + 'ing Rule.', 'error');
              }
            }
          } else {
            this.pagerBusy = false;
          }
        },
        error: (error: any) => {
          console.error('Error updating statuses:', error);
          this.pagerBusy = false;
          if (Helpers && Helpers.smallAlertBox) {
            Helpers.smallAlertBox('Error', 'Problem while ' + type + 'ing Rule.', 'error');
          }
        }
      });
  }

  /**
   * Update rule
   */
  private updateRule(ruleDetail: Rule, freezeScreen?: boolean): void {
    if (freezeScreen) {
      this.pagerBusy = true;
    }

    if (ruleDetail.id && ruleDetail.id !== "") {
      // Update existing rule
      this.updateExistingRule(ruleDetail, (statusesWithRule: Status[]) => {
        this.updateStatuses(ruleDetail.is_deleted ? 'delet' : 'updat', statusesWithRule);
      });
    } else {
      // Add new rule
      ruleDetail.is_enabled = false;
      this.setRuleWithinStatus(ruleDetail, (statusesWithRule: Status[]) => {
        this.updateStatuses('add', statusesWithRule);
      });
    }
  }

  /**
   * Update existing rule
   */
  private updateExistingRule(ruleDetail: Rule, callback: (statuses: Status[]) => void): void {
    const oldRuleConfig = _.find(this.statusRulesBackup, { id: ruleDetail.id });
    if (!oldRuleConfig) {
      callback([]);
      return;
    }

    let configToUpdate = _.cloneDeep(ruleDetail);
    configToUpdate.status_list =  []
    _.each(ruleDetail.status_list, (status: any) => {
      if(status && !_.isEmpty(status) && status.uuid && status.uuid !== "") {
        configToUpdate.status_list.push(status.uuid);
      }else if(status && typeof status === 'string' && status !== ""){
        configToUpdate.status_list.push(status);
      }
    });

    const statusesToUpdate: Status[] = [];

    if (!ruleDetail.created_date && oldRuleConfig.created_date) {
      ruleDetail.created_date = oldRuleConfig.created_date;
    }
    if (!ruleDetail.hasOwnProperty('is_enabled') && oldRuleConfig.hasOwnProperty('is_enabled')) {
      ruleDetail.is_enabled = oldRuleConfig.is_enabled;
    }

    let statusIdsToUnsetRuleFrom: string[] = [];
    let statusIdsToSetRuleTo: string[] = [];

    if (ruleDetail.is_deleted) {
      statusIdsToUnsetRuleFrom = oldRuleConfig.status_list || [];
    } else {
      statusIdsToUnsetRuleFrom = _.difference(oldRuleConfig.status_list || [], configToUpdate.status_list || []);
      statusIdsToSetRuleTo = _.difference(configToUpdate.status_list || [], statusIdsToUnsetRuleFrom);
    }

    // Remove rules from statuses
    _.each(statusIdsToUnsetRuleFrom, (statusId: string) => {
      const statusDetail = _.find(this.statusListBackup, { uuid: statusId });
      if (statusDetail && statusDetail.rules) {
        statusDetail.rules = _.filter(statusDetail.rules, (rule: Rule) => {
          return rule.id !== ruleDetail.id;
        });
      }
      if (statusDetail) {
        statusesToUpdate.push(statusDetail);
      }
    });

    // Update or add rules to statuses
    _.each(statusIdsToSetRuleTo, (statusId: string) => {
      const statusDetail = _.find(this.statusListBackup, { uuid: statusId });
      if (statusDetail) {
        if (!statusDetail.rules || !statusDetail.rules.length) {
          statusDetail.rules = [];
          statusDetail.rules.push(ruleDetail);
        }
        statusDetail.rules = _.filter(statusDetail.rules, (rule: Rule) => {
          if (rule.id === ruleDetail.id) {
            const newRule = Object.assign(rule, ruleDetail);
            delete newRule.status_list;
            return newRule;
          }
          return rule;
        });
        statusesToUpdate.push(statusDetail);
      }
    });

    callback(statusesToUpdate);
  }

  /**
   * Set rule within status
   */
  private setRuleWithinStatus(rule: Rule, callback: (statuses: Status[]) => void): void {
    const statusToUpdate: Status[] = [];
    rule.created_date = new Date();
    rule.id = this.generateId('lw_status_rule');

    const ruleToAdd = _.cloneDeep(rule);
    delete ruleToAdd.status_list;

    _.each(rule.status_list, (status: any) => {
      if (status && !_.isEmpty(status) && status.uuid && status.uuid !== "") {
        const statusDetail = _.find(this.statusListBackup, { uuid: status.uuid });
        if (statusDetail) {
          if (!statusDetail.rules) {
            statusDetail.rules = [];
          }
          statusDetail.rules.push(ruleToAdd);
          statusToUpdate.push(statusDetail);
        }
      }

    });

    callback(statusToUpdate);
  }

  /**
   * Generate unique ID
   */
  private generateId(idPrefix: string): string {
    return idPrefix.toString() + '_' + (new Date()).getTime().toString() + this.getRandomString();
  }

  /**
   * Get random string
   */
  private getRandomString(): string {
    return Math.random().toString(36).substring(2, 15);
  }

  /**
   * Reset and close modal
   */
  private resetAndCloseModal(): void {
    this.resetPaging();
    this.getStatusList();
    this.resetModal();
    this.closeModal();
  }

  /**
   * Reset paging
   */
  private resetPaging(): void {
    this.pagination.statusRulesPageNo = 0;
    this.pagination.statusRulesPageFrom = 1;
    this.pagination.disableNext = true;
    this.pagination.disablePrev = true;
  }

  /**
   * Reset modal
   */
  private resetModal(): void {
    this.pagerBusy = false;
  }

  /**
   * Close modal
   */
  closeModal(): void {
    this.isModalOpen = false;
    this.modalSchema = {};
    this.defaultValues = {};
    this.selectedStatusDetails = null;
    this.selectedRuleDetails = null;
  }

  /**
   * Disable drag while searching, saving, or in readonly mode
   */
  isDragDisabled(): boolean {
    return this.readonly || this.pagerBusy || !!(this.lwActionStatusBasicSearch && this.lwActionStatusBasicSearch.trim());
  }

  trackByStatusId(_index: number, status: Status): string {
    return status?.uuid || String(_index);
  }

  /**
   * Reorder sequenced statuses (matching old lw-table-renderer rearrageRows)
   */
  onStatusDrop(event: CdkDragDrop<Status[]>): void {
    if (this.readonly) {
      Helpers.smallAlertBox('Access denied', 'You do not have write permission', 'info', 1500);
      return;
    }
    if (event.previousIndex === event.currentIndex || this.isDragDisabled()) {
      return;
    }

    moveItemInArray(this.statusList.sequenced, event.previousIndex, event.currentIndex);
    this.statusList.sequenced.forEach((status: Status, index: number) => {
      status.sequence = index + 1;
    });
    this.sequenceChange(this.statusList.sequenced);
  }

  /**
   * Handle sequence change
   */
  sequenceChange(list: Status[]): void {
    this.pagerBusy = true;
    this.updateStatuses('updat', list, true);
  }

  /**
   * Toggle rule active state
   */
  toggleRule(rule: Rule): void {
    rule.is_enabled = !rule.is_enabled;
    rule.last_updated_on = new Date();
    let ruleDetail = _.cloneDeep(rule);
    const statusList = [];
    this.updateRule(rule, true);
  }

  /**
   * On search change
   */
  onSearchChange(): void {
    if (this.viewMode === 'basic') {
      this.searchStatus(this.lwActionStatusBasicSearch, 'statusList');
    } else {
      this.searchStatus(this.lwActionStatusAdvancedSearch, 'statusRules');
    }
  }

  /**
   * Close banner
   */
  closeBanner(): void {
    // Banner close logic if needed
  }

  /**
   * Can deactivate guard implementation
   */
  canDeactivate(): Promise<boolean> | boolean {
    // Implementation for unsaved changes check
    return true;
  }
}
